// src/components/common/GameListItem.jsx — Compact list row for mobile
import { useState } from "react";
import { Link } from "react-router-dom";

const ACC_CFG = {
  steam:        { label:"Steam",   icon:"🎮", color:"#1b9aff", bg:"rgba(27,154,255,0.12)",  border:"rgba(27,154,255,0.25)" },
  epic:         { label:"Epic",    icon:"⬛", color:"#e0e0e0", bg:"rgba(224,224,224,0.08)", border:"rgba(224,224,224,0.18)" },
  game_account: { label:"Account", icon:"👤", color:"#a78bfa", bg:"rgba(167,139,250,0.12)", border:"rgba(167,139,250,0.25)" },
  shared:       { label:"Shared",  icon:"🔗", color:"#34d399", bg:"rgba(52,211,153,0.1)",   border:"rgba(52,211,153,0.22)" },
  private:      { label:"Private", icon:"🔒", color:"#fbbf24", bg:"rgba(251,191,36,0.1)",   border:"rgba(251,191,36,0.22)" },
  family:       { label:"Family",  icon:"👨‍👩‍👧", color:"#f97316", bg:"rgba(249,115,22,0.1)",  border:"rgba(249,115,22,0.22)" },
};

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.gli {
  display:flex; align-items:center; gap:12px;
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.06);
  border-radius:10px; padding:10px 12px; text-decoration:none; color:#fff;
  font-family:'Inter',sans-serif;
  transition:background 0.15s, border-color 0.15s;
  animation:gliIn 0.3s ease both;
  position:relative; overflow:hidden;
}
.gli:active { background:#222; }
@keyframes gliIn {
  from { opacity:0; transform:translateX(-10px); }
  to   { opacity:1; transform:translateX(0); }
}
.gli.oos { opacity:0.5; }

/* Cover thumbnail */
.gli-thumb {
  width:56px; height:72px; border-radius:6px; flex-shrink:0;
  overflow:hidden; background:#111; position:relative;
}
.gli-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
.gli-thumb-placeholder {
  width:100%; height:100%; display:flex; align-items:center; justify-content:center;
  font-size:24px; background:linear-gradient(135deg,#1a1a1a,#2a2a2a);
}

/* Stock dot on thumb */
.gli-stock-dot {
  position:absolute; bottom:4px; right:4px;
  width:8px; height:8px; border-radius:50%;
}
.gli-stock-dot.in  { background:#22c55e; }
.gli-stock-dot.low { background:#eab308; }
.gli-stock-dot.out { background:#ef4444; }

/* Main content */
.gli-body { flex:1; min-width:0; display:flex; flex-direction:column; gap:4px; }
.gli-title {
  font-size:14px; font-weight:700; color:#fff;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.gli-meta { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.gli-acc-pill {
  display:inline-flex; align-items:center; gap:3px;
  font-size:10px; font-weight:600;
  padding:2px 6px; border-radius:4px;
}
.gli-genre {
  font-size:10px; font-weight:500; color:#6b6b6b;
  background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
  border-radius:4px; padding:1px 5px;
}
.gli-stock-label { font-size:10px; color:#6b6b6b; }
.gli-stock-label.low { color:#eab308; }
.gli-stock-label.out { color:#ef4444; }

/* Right side */
.gli-right { display:flex; flex-direction:column; align-items:flex-end; gap:4px; flex-shrink:0; }
.gli-price { font-size:17px; font-weight:800; color:#0078F2; }
.gli-rating { font-size:12px; font-weight:600; color:#eab308; }
.gli-arrow { font-size:16px; color:#3a3a3a; }
`;

export default function GameListItem({ game, index = 0 }) {
  const isOOS = game.stock <= 0;
  const isLow = !isOOS && game.stock <= 2;
  const acc = ACC_CFG[game.accountType] || ACC_CFG.steam;
  const stockClass = isOOS ? "out" : isLow ? "low" : "in";

  return (
    <>
      <style>{S}</style>
      <Link
        to={`/game/${game.id}`}
        className={`gli ${isOOS ? "oos" : ""}`}
        style={{ animationDelay:`${index * 0.04}s` }}
      >
        {/* Thumbnail */}
        <div className="gli-thumb">
          {game.coverImage
            ? <img src={game.coverImage} alt={game.title} />
            : <div className="gli-thumb-placeholder">🎮</div>
          }
          <div className={`gli-stock-dot ${stockClass}`} />
        </div>

        {/* Body */}
        <div className="gli-body">
          <div className="gli-title">{game.title}</div>
          <div className="gli-meta">
            <span
              className="gli-acc-pill"
              style={{ background:acc.bg, border:`1px solid ${acc.border}`, color:acc.color }}
            >
              {acc.icon} {acc.label}
            </span>
            {(game.genre||[]).slice(0,1).map(g => (
              <span key={g} className="gli-genre">{g}</span>
            ))}
          </div>
          <div className={`gli-stock-label ${stockClass}`}>
            {isOOS ? "Out of stock" : isLow ? `Only ${game.stock} left` : "In stock"}
          </div>
        </div>

        {/* Right */}
        <div className="gli-right">
          <div className="gli-price">${game.price?.toFixed(2)}</div>
          {game.rating && <div className="gli-rating">★ {game.rating}</div>}
          <div className="gli-arrow">›</div>
        </div>
      </Link>
    </>
  );
}